import { useCallback, useEffect } from "react"
import { ChartsWidget1 } from "../../../_metronic/partials/widgets";
import { am4charts, am4core, initAmCharts } from "./AmCharts";

export default function ClusteredChart({
  id = "clusteredChartDiv",
  data = [],
  className,
  category,
  barPrimary,
  barSecondary,
  barPrimaryName,
  barSecondaryName,
  barPrimaryTooltip,
  barSecondaryTooltip,
  barPrimaryLabelBulletText,
  barSecondaryLabelBulletText,
  barPrimaryLabelBulletDy = -15,
  barSecondaryLabelBulletDy = -50,
  maskBullets = true,
  paddingTop = 15,
  horizontal = false,
  title
}) {

  useEffect(() => {
    initAmCharts();
  }, [])

  const chartLabelBullet = useCallback((labelBulletText, labelBulletDy, rotation) => {
    let labelBullet = new am4charts.LabelBullet();
    labelBullet.label.text = labelBulletText;
    labelBullet.label.fontSize = 12;
    labelBullet.label.rotation = rotation;
    labelBullet.label.dy = labelBulletDy;
    labelBullet.label.truncate = false;
    return labelBullet;
  }, [])

  // Configurar las barras agrupadas
  const chartColumnSeries = useCallback((barValue, categoryX, barName, barTooltipText, valueAxis, horizontal) => {
    let columnSeries = new am4charts.ColumnSeries();
    if (horizontal) {
      columnSeries.dataFields.valueX = barValue;
      columnSeries.dataFields.categoryY = categoryX;
      columnSeries.xAxis = valueAxis; // Asociar la serie a su propio eje Y cambiar para vertical

    } else {
      columnSeries.dataFields.valueY = barValue;
      columnSeries.dataFields.categoryX = categoryX;
      columnSeries.yAxis = valueAxis; // Asociar la serie a su propio eje Y cambiar para vertical
    }
    columnSeries.name = barName;
    columnSeries.columns.template.tooltipText = barTooltipText;
    columnSeries.tooltip.getFillFromObject = false;
    columnSeries.tooltip.background.fill = am4core.color("#fff");
    columnSeries.tooltip.label.fill = am4core.color("#000");
    return columnSeries;
  }, [])



  // Configurar categorías en el eje X
  const categoryAxisConfig = useCallback((categoryAxisObj) => {
    let categoryAxis = categoryAxisObj;
    categoryAxis.dataFields.category = category;
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.minGridDistance = 30;
    categoryAxis.renderer.cellStartLocation = 0.1;
    categoryAxis.renderer.cellEndLocation = 0.9;
    categoryAxis.renderer.labels.template.rotation = 0;
    categoryAxis.clustered = false;
    categoryAxis.renderer.grid.template.disabled = true;
  }, [category])


  const valueAxisPrimaryConfig = useCallback((valueAxisObj) => {
    let valueAxis = valueAxisObj;
    valueAxis.renderer.minWidth = 40; // Ajusta el ancho del eje Y si es necesario
    // Desactivar las lineas que interceptan el eje Y
    // valueAxis1.renderer.grid.template.disabled = true;
    // Agregar linea a la regla del eje Y
    // valueAxis1.renderer.line.strokeOpacity = 1;
    // valueAxis1.renderer.line.strokeWidth = 0.2;
    // valueAxis1.renderer.line.stroke = am4core.color("#000");
    return valueAxis;
  }, [])

  const seriesPrimaryConfig = useCallback((chart, valueAxis, horizontal) => {
    return chart.series.push(chartColumnSeries(barPrimary, category, barPrimaryName, barPrimaryTooltip, valueAxis, horizontal));
  }, [barPrimary, barPrimaryName, barPrimaryTooltip, category, chartColumnSeries])

  const labelBulletPrimaryConfig = useCallback((series) => {
    if (barPrimaryLabelBulletText) {
      series.bullets.push(chartLabelBullet(barPrimaryLabelBulletText, barPrimaryLabelBulletDy, 300));
    }
  }, [barPrimaryLabelBulletDy, barPrimaryLabelBulletText, chartLabelBullet])


  const valueAxisSecondaryConfig = useCallback((valueAxisObj) => {
    let valueAxis = valueAxisObj;
    valueAxis.renderer.opposite = true;
    valueAxis.renderer.minWidth = 40;
    return valueAxis;
  }, [])

  const seriesSecondaryConfig = useCallback((chart, valueAxis, horizontal) => {
    return chart.series.push(chartColumnSeries(barSecondary, category, barSecondaryName, barSecondaryTooltip, valueAxis, horizontal));
  }, [barSecondary, barSecondaryName, barSecondaryTooltip, category, chartColumnSeries])

  const labelBulletSecondaryConfig = useCallback((series) => {
    if (barSecondaryLabelBulletText) {
      series.bullets.push(chartLabelBullet(barSecondaryLabelBulletText, barSecondaryLabelBulletDy, 280));
    }
  }, [barSecondaryLabelBulletDy, barSecondaryLabelBulletText, chartLabelBullet])


  const titleConfig = useCallback((chart) => {
    let chartTitle = chart.titles.create();
    chartTitle.text = title;
    chartTitle.fontSize = 16;
    chartTitle.paddingBottom = 30;
  }, [title])

  const labelAxisVerticalChartConfig = useCallback((chart) => {
    let label = chart.createChild(am4core.Label);
    label.text = barPrimaryName;
    label.fontSize = 14;
    label.isMeasured = false;
    label.x = 0;
    label.y = am4core.percent(55);
    label.rotation = 270;

    let label2 = chart.createChild(am4core.Label);
    label2.text = barSecondaryName;
    label2.fontSize = 14;
    label2.isMeasured = false;
    label2.x = am4core.percent(102);
    label2.y = am4core.percent(40);
    label2.rotation = 90;
  }, [barPrimaryName, barSecondaryName])

  const labelAxisHorizontalChartConfig = useCallback((chart) => {
    let label = chart.createChild(am4core.Label);
    label.text = barPrimaryName;
    label.fontSize = 14;
    label.isMeasured = false;
    label.x = am4core.percent(58);
    label.y = am4core.percent(10);


    let label2 = chart.createChild(am4core.Label);
    label2.text = barSecondaryName;
    label2.fontSize = 14;
    label2.isMeasured = false;
    label2.x = am4core.percent(57);
    label2.y = am4core.percent(84);
  }, [barPrimaryName, barSecondaryName])


  const chartConfig = useCallback((chart) => {
    chart.maskBullets = maskBullets;
    chart.paddingTop = paddingTop;
    chart.paddingRight = 30;
    // Configurar leyenda
    chart.legend = new am4charts.Legend();
    if (horizontal) {
      chart.legend.marginTop = 20;
    }
  }, [horizontal, maskBullets, paddingTop])

  useEffect(() => {
    let chart = am4core.create(id, am4charts.XYChart),
      valueAxisPrimary = null,
      valueAxisSecondary = null;

    chart.data = data;

    if (horizontal) {
      categoryAxisConfig(chart.yAxes.push(new am4charts.CategoryAxis()));

      valueAxisSecondary = valueAxisSecondaryConfig(chart.xAxes.push(new am4charts.ValueAxis()));
      valueAxisPrimary = valueAxisPrimaryConfig(chart.xAxes.push(new am4charts.ValueAxis()));

      valueAxisSecondary.renderer.opposite = false;
      valueAxisPrimary.renderer.opposite = true;

      labelAxisHorizontalChartConfig(chart);
    } else {
      categoryAxisConfig(chart.xAxes.push(new am4charts.CategoryAxis()));

      valueAxisPrimary = valueAxisPrimaryConfig(chart.yAxes.push(new am4charts.ValueAxis()));

      valueAxisSecondary = valueAxisSecondaryConfig(chart.yAxes.push(new am4charts.ValueAxis()));

      labelAxisVerticalChartConfig(chart);
    }

    const seriesPrimary = seriesPrimaryConfig(chart, valueAxisPrimary, horizontal);
    labelBulletPrimaryConfig(seriesPrimary);

    const seriesSecondary = seriesSecondaryConfig(chart, valueAxisSecondary, horizontal);
    labelBulletSecondaryConfig(seriesSecondary);

    titleConfig(chart);


    chartConfig(chart);

    return () => {
      chart.dispose();
    };

  }, [categoryAxisConfig, chartConfig, data, horizontal, id, labelAxisHorizontalChartConfig, labelAxisVerticalChartConfig, labelBulletPrimaryConfig, labelBulletSecondaryConfig, seriesPrimaryConfig, seriesSecondaryConfig, titleConfig, valueAxisPrimaryConfig, valueAxisSecondaryConfig])

  return (
    <>
      <div id={id} className={className}></div>
    </>
  )
}